import React from 'react';
import { useRegime } from '../../contexts/RegimeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAnalyticsSnapshotOverride } from './RegimeSnapshotBridge';

function Chip({
  label,
  value,
  warn,
}: {
  label: string;
  value: string;
  warn?: boolean;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        gap: 1,
        padding: '4px 8px',
        borderRadius: 'var(--radius-sm)',
        background: warn ? 'var(--down-dim)' : 'var(--elev2)',
        border: `1px solid ${warn ? 'color-mix(in srgb, var(--down) 45%, transparent)' : 'var(--hair)'}`,
        minWidth: 72,
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '.08em',
          color: 'var(--muted-2)',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          color: warn ? 'var(--down)' : 'var(--txt)',
        }}
      >
        {value}
      </span>
    </span>
  );
}

/** Tier-1 GEX / vol chips for tool headers. */
const GexAnalyticsStrip: React.FC = () => {
  const { snapshot: regimeSnapshot, loading } = useRegime();
  const overrideSnapshot = useAnalyticsSnapshotOverride();
  const snapshot = overrideSnapshot ?? regimeSnapshot;
  const { language } = useLanguage();
  const ar = language === 'ar';
  if (!snapshot && loading) return null;
  if (!snapshot) return null;

  const g = snapshot.gex;
  const v = snapshot.vol_flow;
  const fmt = (n: number | null, digits = 2) =>
    n == null ? '—' : Math.abs(n) >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n.toFixed(digits);

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        alignItems: 'center',
        marginBottom: 8,
      }}
    >
      <Chip label={ar ? 'دولار GEX' : 'dollar_gex'} value={fmt(g.dollar_gex, 0)} />
      <Chip label="gex_z" value={g.gex_zscore == null ? '—' : g.gex_zscore.toFixed(2)} warn={g.gex_zscore != null && g.gex_zscore < -1} />
      <Chip label={ar ? 'فخ' : 'trap'} value={g.trap_zone ? (ar ? 'نعم' : 'YES') : '—'} warn={g.trap_zone} />
      <Chip label={ar ? 'تثبيت' : 'pin'} value={g.pin_probability == null ? '—' : `${Math.round(g.pin_probability * 100)}%`} />
      <Chip label="zVoV" value={v.z_vov == null ? '—' : v.z_vov.toFixed(2)} warn={v.z_vov != null && v.z_vov > 1.5} />
      <Chip label="IV-HAR" value={v.iv_har_gap == null ? '—' : v.iv_har_gap.toFixed(3)} />
      <Chip label="PCR" value={v.signed_buyer_pcr == null ? '—' : v.signed_buyer_pcr.toFixed(2)} />
      <Chip label="VPIN" value={v.vpin_rolling == null ? '—' : v.vpin_rolling.toFixed(2)} warn={v.flow_toxicity} />
      <Chip label="γ accel" value={g.gamma_acceleration == null ? '—' : g.gamma_acceleration.toExponential(2)} />
      <Chip label={ar ? 'دقائق' : 'min→close'} value={String(g.minutes_to_close)} />
    </div>
  );
};

export default GexAnalyticsStrip;
